<template>
	<div id="account-downloads-wrapper" class="account-data-cell" v-if="canDownloadSomething">

		<div class="modal-button-wrapper">
			<b-button
				@click.prevent="showDownloadOptions"
				class="has-icon"
				icon-left="download"
				type="is-success">
				Download
			</b-button>

			<p>All documents size: {{ fileSize }}</p>
		</div>

		<div class="download-progress-wrapper" v-if="organizedDownloads.length || unorganizedDownloads.length">
			<b-notification type="is-light" :closable="false"
			                v-for="downloadProgress in [...organizedDownloads, ...unorganizedDownloads]"
			                :key="downloadProgress.id">
				<div class="zip-generation-status">
					<h6>
						<strong>Generating Zip</strong>
						<div class="percent-complete pull-right">
							{{ getPercentComplete(downloadProgress.progress.percentage_complete) }}%
						</div>
					</h6>

					<p class="is-size-7">
						{{ downloadProgress.progress.status }}
						<template v-if="getPercentComplete(downloadProgress.progress.percentage_complete) < 100">:
							{{ downloadProgress.progress.files_processed }}
							of {{ downloadProgress.progress.total_files }}
						</template>
					</p>

					<div class="zip-progress-bar-wrapper m-t-15">
						<progress-bar :value="getPercentComplete(downloadProgress.progress.percentage_complete)" />
					</div>
				</div>
			</b-notification>
		</div>

		<b-modal :active.sync="isSelectingDownload">
			<div class="modal-card download-options-modal">
				<header class="modal-card-head">
					<p class="modal-card-title">Select Your Downloads</p>
				</header>
				<section class="modal-card-body">
					<p>
						Click a download option below. Some files may take a moment to generate before downloading.
						Generating progress will be displayed after making your selection.
					</p>

					<div id="download-options">
						<b-loading :is-full-page="false" :active.sync="isProcessingDownload">
							<div class="loading-content">
								<div class="fa-bouncing">
									<b-icon
										pack="fas"
										icon="download"
										size="is-large">
									</b-icon>
								</div>
								<p>One moment, preparing your download...</p>
							</div>
						</b-loading>

						<div class="download-option"
						     v-if="userCan('view_download_document_list_button', 'accounts')"
						     v-on:click="performDownload('download-document-list')"
						>
							<div class="download-option--icon icon-word"><b-icon icon="file-word" size="is-large"></b-icon></div>
							<div class="download-option--display">
								<div class="download-option--label">Disclosed Document List</div>
								<div class="download-option--hint">A document key containing names, dates, authors, and property associations.</div>
							</div>
							<div class="download-option--action">
								<b-icon icon="download"></b-icon>
							</div>
						</div>

						<div class="download-option"
						     v-if="userCan('view_download_property_list_button', 'accounts')"
						     v-on:click="performDownload('download-property-list')"
						>
							<div class="download-option--icon icon-excel"><b-icon icon="file-excel" size="is-large"></b-icon></div>
							<div class="download-option--display">
								<div class="download-option--label">Property List</div>
								<div class="download-option--hint">Property addresses with other datapoints as provided.</div>
							</div>
							<div class="download-option--action">
								<b-icon icon="download"></b-icon>
							</div>
						</div>

						<div class="download-option"
						     v-if="userCan('view_download_property_list_button', 'accounts')"
						     v-on:click="performDownload('download-property-list-documents')"
						>
							<div class="download-option--icon icon-excel"><b-icon icon="file-excel" size="is-large"></b-icon></div>
							<div class="download-option--display">
								<div class="download-option--label">Properties with Documents</div>
								<div class="download-option--hint">Indicating properties WITH associated documents and those WITHOUT.</div>
							</div>
							<div class="download-option--action">
								<b-icon icon="download"></b-icon>
							</div>
						</div>

						<template v-if="userCan('view_download_all_files_button', 'accounts')">
							<div class="download-option" v-on:click="performDownload('download-all-files-organized')">
								<b-loading :is-full-page="false" :active.sync="hasOrganizedFileArchivePending">
									<div class="loading-content-zip">
										<p>Download is being generated in the background...</p>
										<template v-for="(downloadProgress, index) in organizedDownloads">
											<progress-bar
												:key="`organized-dp-` + index"
												:value="getPercentComplete(downloadProgress.progress.percentage_complete)"
											/>
										</template>
									</div>
								</b-loading>

								<div class="download-option--icon icon-archive"><b-icon icon="file-archive" size="is-large"></b-icon></div>
								<div class="download-option--display">
									<div class="download-option--label">Generate Organized Zip</div>
									<div class="download-option--hint">Archive of all documents organized by property address.</div>
								</div>
								<div class="download-option--action">
									<b-icon icon="download"></b-icon>
								</div>
							</div>

							<div class="download-option last" v-on:click="performDownload('download-all-files-unorganized')">
								<b-loading :is-full-page="false" :active.sync="hasUnorganizedFileArchivePending">
									<div class="loading-content-zip">
										<p>Download is being generated in the background...</p>
										<template v-for="(downloadProgress, index) in unorganizedDownloads">
											<progress-bar
												:key="`unorganized-dp-` + index"
												:value="getPercentComplete(downloadProgress.progress.percentage_complete)"
											/>
										</template>
									</div>
								</b-loading>

								<div class="download-option--icon icon-archive"><b-icon icon="file-archive" size="is-large"></b-icon></div>
								<div class="download-option--display">
									<div class="download-option--label">Generate Unorganized Zip</div>
									<div class="download-option--hint">Archive of all documents in a single folder.</div>
								</div>
								<div class="download-option--action">
									<b-icon icon="download"></b-icon>
								</div>
							</div>
						</template>
					</div>
				</section>
				<footer class="modal-card-foot">
					<button class="button is-primary" type="button" @click="isSelectingDownload = false" v-if="downloadedAnything">Back to Account</button>
					<button class="button" type="button" @click="isSelectingDownload = false" v-else>Cancel</button>
				</footer>
			</div>
		</b-modal>
	</div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import {
  ACCOUNT_FILES_GET,
  ACCOUNT_FILE_ACTION,
  ACCOUNT_FILE_GET,
  ACCOUNT_EXPORT_DOWNLOAD
} from '@/store/actions/accounts'
import { DATATABLE_ACTION } from '@/store/actions/datatable'
import generateDocList from '@/functions/generateDocList'
import ProgressBar from '@/components/Elements/ProgressBar'

export default {
  name: 'AccountDownloads',
  components: {
    ProgressBar
  },
  data: function () {
    return {
      loading: false,
      isSelectingDownload: false,
      isProcessingDownload: false,
      downloadedAnything: false
    }
  },
  props: ['accountId', 'accountName', 'accountNumber', 'fileSize'],
  watch: {
    isSelectingDownload () {
      setTimeout(() => {
        this.downloadedAnything = false
      }, 500)
    }
  },
  computed: {
    ...mapGetters({
      userCan: 'userCan',
      hasOrganizedFileArchivePending: 'hasOrganizedFileArchivePending',
      hasUnorganizedFileArchivePending: 'hasUnorganizedFileArchivePending',
      organizedDownloads: 'organizedDownloads',
      unorganizedDownloads: 'unorganizedDownloads'
    }),
    canDownloadSomething () {
      if (this.userCan('view_download_document_list_button', 'accounts')) return true
      if (this.userCan('view_download_property_list_button', 'accounts')) return true
      if (this.userCan('view_download_all_files_button', 'accounts')) return true

      return false
    }
  },
  methods: {
    handleError (err) {
      if (err.hasOwnProperty('response')) {
        if (err.response.data.response === 'error') {
          this.$awn.warning(err.response.data.message)
        }
      } else {
        this.$awn.alert(err.toString())
      }
    },
    downloadDocumentList () {
      this.loading = true
      this.$store.dispatch(ACCOUNT_FILES_GET, {
        'account_id': this.accountId,
        'paginate': 9999,
        'filter__private': 0,
        'filter__sov': 0,
        'filter__provided_by_broker': 0
      }).then(response => {
        if (response.meta.total === 0) {
          this.$buefy.dialog.alert({
            title: 'Disclosed Document List Error',
            message: 'This account does not have any <strong>disclosed documents</strong> to generate the list from.',
            type: 'is-warning',
            hasIcon: true,
            icon: 'exclamation-triangle',
            iconPack: 'fa'
          })

          return
        }
        generateDocList(
          response, this.accountName + ' - Disclosed Document List.docx',
          this.accountName + ' - Disclosed Document List'
        )
      }).finally(() => {
        this.loading = false
        this.isProcessingDownload = false
      })
    },
    downloadPropertyList (withDocuments = false) {
      const data = {
        handle: 'json',
        model: 'account_properties',
        selectedIds: [],
        action: 'export_excel',
        fileName: this.accountName + ' - Properties.xlsx',
        input: {
          all: 1,
          with_documents: withDocuments,
          account_id: this.accountId
        }
      }

      this.loading = true
      this.$store.dispatch(DATATABLE_ACTION, data)
        .then(response => {
          if (response) {
            if (response.message !== undefined) {
              this.$awn.success(response.message)
            }

            if (response.export) {
              this.$store.dispatch(ACCOUNT_EXPORT_DOWNLOAD, { model: response.export })
            }
          }
        })
        .catch(err => {
          console.error(err)
          this.$awn.alert('No properties found')
        })
        .finally(() => {
          this.loading = false
          this.isProcessingDownload = false
        })
    },
    downloadAllFiles (organized = true) {
      this.loading = true

      const data = {
        'account_id': this.accountId,
        'params': {
          'action': 'archive',
          'organized': organized
        }
      }

      let source = organized ? 'downloadall-organized' : 'downloadall-unorganized'

      this.$store.dispatch(ACCOUNT_FILE_ACTION, data).then(response => {
        if (response.data.status !== 'accessible') {
          this.$store.commit('addPendingDownload', {
            id: response.data.id,
            accountId: response.data.account_id,
            source: source,
            progress: _.get(response.data, 'progress', null)
          })

          this.$awn.success(response.message)
        } else {
          const data = {
            account_id: response.data.account_id,
            file_name: response.data.file.original_file_name,
            file_id: response.data.id
          }
          this.$store.dispatch(ACCOUNT_FILE_GET, data).then(response => {
            const url = response.data.url
            const link = document.createElement('a')
            link.href = url
            document.body.appendChild(link)
            link.click()
            link.parentNode.removeChild(link)
          })
        }
      }).finally(() => {
        this.loading = false
        this.isProcessingDownload = false
      }).catch(err => {
        this.handleError(err)
      })
    },
    getPercentComplete (percentComplete) {
      if (percentComplete === 0) return 1
      if (percentComplete >= 100) return 100

      return percentComplete
    },
    showDownloadOptions () {
      this.isSelectingDownload = true
    },
    performDownload (action) {
      this.isProcessingDownload = true
      this.downloadedAnything = true

      if (action === 'download-document-list') {
        this.downloadDocumentList()
      }

      if (action === 'download-property-list') {
        this.downloadPropertyList(false)
      }

      if (action === 'download-property-list-documents') {
        this.downloadPropertyList(true)
      }

      if (action === 'download-all-files-organized') {
        this.downloadAllFiles()
      }

      if (action === 'download-all-files-unorganized') {
        this.downloadAllFiles(false)
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Account/AccountDownloads.vue